import { lookupKey, normalizeSanskrit, tokenizeSentence } from '@/sanskrit/lib/sanskrit/script'
import { bhashiniActive, speakBhashini, stopBhashini } from '@/lib/client/pronunciation'

/**
 * Audio abstraction layer.
 *
 * Order of preference for any Sanskrit text:
 *   1. A curated human recording registered in RECORDINGS (none are bundled yet;
 *      teachers can add them and they take over automatically).
 *   2. The voice chosen in the shared Suvaani preferences: Bhashini (Premium, a
 *      Sanskrit voice where Bhashini has one) or the device's own speech voice
 *      (Sanskrit if the device has one, otherwise the closest Devanagari voice such
 *      as Hindi). The device voice is labelled approximate, and it also reads
 *      whenever Bhashini cannot.
 *   3. Nothing: the UI says audio is unavailable instead of pretending.
 */

export type AudioKind = 'recording' | 'bhashini' | 'device-voice' | 'unavailable'

export interface AudioInfo {
    kind: AudioKind
    voiceName?: string
    voiceLang?: string
    approximate: boolean
}

const RECORDINGS = new Map<string, string>()

/** Register curated recordings: normalised Sanskrit text → audio URL. */
export const registerRecordings = (entries: Record<string, string>) => {
    for (const [text, url] of Object.entries(entries)) RECORDINGS.set(lookupKey(text), url)
}

export const recordingFor = (text: string): string | undefined => RECORDINGS.get(lookupKey(text))

const PREFERRED_LANGS = ['sa', 'hi', 'mr', 'ne']

export const speechSupported = (): boolean => typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.SpeechSynthesisUtterance === 'function'

/** Call `fn` whenever the device's voice list changes; returns the function that stops listening. */
export const onVoicesChanged = (fn: () => void): (() => void) => {
    if (!speechSupported()) return () => undefined

    window.speechSynthesis.addEventListener('voiceschanged', fn)

    return () => window.speechSynthesis.removeEventListener('voiceschanged', fn)
}

export const devanagariVoices = (): SpeechSynthesisVoice[] => {
    if (!speechSupported()) return []

    return window.speechSynthesis
        .getVoices()
        .filter(v => PREFERRED_LANGS.some(l => v.lang.toLowerCase().startsWith(l)))
        .sort((a, b) => PREFERRED_LANGS.findIndex(l => a.lang.toLowerCase().startsWith(l)) - PREFERRED_LANGS.findIndex(l => b.lang.toLowerCase().startsWith(l)))
}

export const pickVoice = (preferredURI?: string | null): SpeechSynthesisVoice | null => {
    const voices = devanagariVoices()
    if (voices.length === 0) return null
    if (preferredURI) {
        const chosen = voices.find(v => v.voiceURI === preferredURI)
        if (chosen) return chosen
    }
    return voices[0]
}

export const audioInfo = (text: string | undefined, preferredURI?: string | null): AudioInfo => {
    if (text && recordingFor(text)) return { kind: 'recording', approximate: false }
    if (bhashiniActive()) return { kind: 'bhashini', voiceName: 'Bhashini', approximate: false }

    const voice = pickVoice(preferredURI)
    if (voice) return { kind: 'device-voice', voiceName: voice.name, voiceLang: voice.lang, approximate: !voice.lang.toLowerCase().startsWith('sa') }

    return { kind: 'unavailable', approximate: true }
}

let currentAudio: HTMLAudioElement | null = null

export const stopSpeaking = () => {
    stopBhashini()

    if (currentAudio) {
        currentAudio.pause()
        currentAudio = null
    }

    if (speechSupported()) window.speechSynthesis.cancel()
}

export interface SpeakOptions {
    rate?: number
    voiceURI?: string | null
    onStart?: () => void
    onEnd?: () => void
}

/** Speak a piece of Sanskrit. Resolves when playback ends (or immediately when unavailable). */
export const speak = async (text: string, opts: SpeakOptions = {}): Promise<AudioKind> => {
    const clean = normalizeSanskrit(text)

    if (typeof window !== 'undefined' && !recordingFor(clean) && bhashiniActive()) {
        stopSpeaking()

        const ok = await speakBhashini(clean, { lang: 'sa', rate: opts.rate, onStart: opts.onStart, onEnd: opts.onEnd })

        if (ok) return 'bhashini'
    }

    return new Promise(resolve => {
        if (typeof window === 'undefined') return resolve('unavailable')

        stopSpeaking()

        const url = recordingFor(clean)

        if (url) {
            const audio = new Audio(url)
            currentAudio = audio
            audio.playbackRate = opts.rate ?? 1
            audio.onplay = () => opts.onStart?.()
            audio.onended = () => {
                opts.onEnd?.()
                resolve('recording')
            }
            audio.onerror = () => {
                opts.onEnd?.()
                resolve('unavailable')
            }
            void audio.play().catch(() => resolve('unavailable'))
            return
        }

        const voice = pickVoice(opts.voiceURI)

        if (!speechSupported() || !voice) {
            opts.onEnd?.()
            return resolve('unavailable')
        }

        const utterance = new SpeechSynthesisUtterance(clean.replace(/[।॥]/g, '. '))
        utterance.voice = voice
        utterance.lang = voice.lang
        utterance.rate = opts.rate ?? 0.85
        utterance.pitch = 1
        utterance.onstart = () => opts.onStart?.()
        utterance.onend = () => {
            opts.onEnd?.()
            resolve('device-voice')
        }
        utterance.onerror = () => {
            opts.onEnd?.()
            resolve('unavailable')
        }

        window.speechSynthesis.speak(utterance)
    })
}

/** Word-by-word playback with a pause between words. `onWord` gets the index of the word being spoken. */
export const speakWords = async (text: string, opts: SpeakOptions & { gapMs?: number; onWord?: (index: number) => void; shouldStop?: () => boolean } = {}) => {
    const words = tokenizeSentence(text).filter(t => t.isWord).map(t => t.text)

    for (let i = 0; i < words.length; i++) {
        if (opts.shouldStop?.()) break
        opts.onWord?.(i)
        const kind = await speak(words[i], { rate: opts.rate, voiceURI: opts.voiceURI })
        if (kind === 'unavailable') break
        await new Promise(r => setTimeout(r, opts.gapMs ?? 350))
    }

    opts.onWord?.(-1)
}

type RecognitionCtor = new () => {
    lang: string
    interimResults: boolean
    maxAlternatives: number
    continuous: boolean
    onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string; confidence: number }>> }) => void) | null
    onerror: ((e: { error: string }) => void) | null
    onend: (() => void) | null
    start: () => void
    stop: () => void
    abort: () => void
}

export const getRecognitionCtor = (): RecognitionCtor | null => {
    if (typeof window === 'undefined') return null
    const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export const recordingSupported = (): boolean => typeof window !== 'undefined' && typeof window.MediaRecorder === 'function' && Boolean(navigator.mediaDevices?.getUserMedia)
